import { useCallback, useEffect, useRef, useState } from "react";
import { mapWithConcurrency } from "../Reports/functions";
import { readConnectionStatus } from "./api";

// How many /connectionStatus calls run at once when a whole store is checked.
const CONCURRENCY = 6;

// Live connection answers for the store on screen: deviceId -> { connected,
// connectedSince }. The Status column prefers these over the device document,
// which is only as fresh as the last read.
//
// `checkMany(ids)` asks for several devices (a store loading, the page
// Refresh), a few at a time; `checkOne(id)` for one (a row refresh, the editor
// opening). A device whose call fails keeps whatever it had, so its row falls
// back to the document. Answers for a store that is no longer on screen are
// dropped, and switching store starts empty.
export const useConnectionStatuses = (propertyCode) => {
  const [statuses, setStatuses] = useState({});
  const currentProperty = useRef(propertyCode);

  useEffect(() => {
    currentProperty.current = propertyCode;
    setStatuses({});
  }, [propertyCode]);

  const checkOne = useCallback(async (deviceId) => {
    const askedFor = currentProperty.current;
    const status = await readConnectionStatus(deviceId);
    if (!status || currentProperty.current !== askedFor) return;
    setStatuses((current) => ({ ...current, [deviceId]: status }));
  }, []);

  const checkMany = useCallback(
    (deviceIds) => mapWithConcurrency(deviceIds, CONCURRENCY, checkOne),
    [checkOne]
  );

  return { statuses, checkOne, checkMany };
};
